import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { get } from "../lib/http";
import { API } from "../config/api.js";
import ReactPaginate from "react-paginate";
import FancyLoader from "../components/FancyLoader.jsx";
import {
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaDownload,
  FaInfoCircle,
  FaSearch,
  FaTimes,
  FaRegCopy,
  FaCheck,
} from "react-icons/fa";

/* ---------------- helpers ---------------- */
const inr = (n) => (typeof n === "number" ? `₹${n.toLocaleString("en-IN")}` : n || "—");
const TZ = "Asia/Kolkata";

function formatDT(
  iso,
  { mode = "preserveBackendWallTime", withTime = true, keepCalendarDate = false } = {}
) {
  if (!iso) return "—";
  const d = new Date(iso);

  if (keepCalendarDate) {
    // Date shown in UTC calendar (backend), time shown in IST
    const date = new Intl.DateTimeFormat("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      timeZone: "UTC",
    }).format(d);

    if (!withTime) return date;

    const time = new Intl.DateTimeFormat("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone: TZ,
    }).format(d);

    return `${date} • ${time}`;
  }

  // Default behavior: both parts same zone per mode
  const timeZone = mode === "localIST" ? TZ : "UTC";

  const date = new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone,
  }).format(d);

  if (!withTime) return date;

  const time = new Intl.DateTimeFormat("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone,
  }).format(d);

  return `${date} • ${time}`;
}

function downloadBlob(blob, filenameFallback = "file.bin") {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filenameFallback;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
function b64toBlob(b64, type) {
  const bin = atob(b64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return new Blob([arr], { type });
}

/* ---------------- icon (ticket) ---------------- */
function TicketIcon({ className = "h-6 w-6" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16v4a2 2 0 0 1 0 4v4H4v-4a2 2 0 0 1 0-4V4z" />
      <path d="M8 4v16M16 4v16" />
    </svg>
  );
}

export default function MyBookingsPage() {
  const [bookings, setBookings] = useState([]);
  const [selected, setSelected] = useState(null);

  // Pagination
  const [page, setPage] = useState(1); // 1-based
  const [limit] = useState(12);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState("");

  // Search
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Copy state map to show "copied" feedback per booking
  const [copiedMap, setCopiedMap] = useState({});

  // Debounce search input (450ms)
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 450);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Reset to page 1 whenever user changes search (immediate)
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    setErrMsg("");
    try {
      const raw = localStorage.getItem("bb_user");
      const parsed = raw ? JSON.parse(raw) : null;
      const userId = parsed?.id ?? null;
      const token = parsed?.token ?? null;

      if (!userId) {
        setBookings([]);
        setTotal(0);
        setTotalPages(1);
        setErrMsg("User not logged in.");
        setLoading(false);
        return;
      }

      // Build URL with pagination and optional search (q)
      const qParam = debouncedSearch ? `&q=${encodeURIComponent(debouncedSearch)}` : "";
      const url = `${API.booking.getAllBooking}?userId=${encodeURIComponent(
        userId
      )}&page=${page}&limit=${limit}&paginate=true${qParam}`;
      
      // const url = `${API.booking.getAllBooking}?page=${page}&limit=${limit}&paginate=true${qParam}`;

      const res = await get(url, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          "Content-Type": "application/json",
        },
      });

      const arr = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
      const meta = res?.meta || (res?.data?.meta ? res.data.meta : {});

      const totalFromMeta =
        Number(meta.total) || (Array.isArray(res) ? res.length : arr.length || 0);
      const totalPagesFromMeta =
        Number(meta.totalPages) || Math.max(1, Math.ceil(totalFromMeta / limit));
      const pageFromMeta = Number(meta.page) || page;

      setBookings(arr);
      setTotal(totalFromMeta);
      setTotalPages(totalPagesFromMeta);
      if (pageFromMeta !== page) setPage(pageFromMeta);
    } catch (e) {
      console.error("fetchBookings error:", e);
      setBookings([]);
      setTotal(0);
      setTotalPages(1);
      setErrMsg(e?.message || "Failed to load bookings.");
    } finally {
      setLoading(false);
    }
  }, [page, limit, debouncedSearch]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const handlePageChange = ({ selected }) => {
    const newPage = selected + 1;
    if (newPage !== page) setPage(newPage);
  };

  async function downloadTicketPDF(bookingCode, eventName) {
    try {
      const token = JSON.parse(localStorage.getItem("bb_user") || "{}").token ?? null;
      const newUrl = import.meta.env.VITE_API_BASE_URL;
      const url = `${newUrl}/api/ticket/downloadTicketsAsPDF/${encodeURIComponent(
        bookingCode
      )}`;
      const resp = await fetch(url, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!resp.ok) {
        const msg = await resp.text().catch(() => "");
        throw new Error(`HTTP ${resp.status} ${msg}`);
      }
      const ct = (resp.headers.get("content-type") || "").toLowerCase();
      let blob;
      if (ct.includes("application/pdf")) blob = await resp.blob();
      else if (ct.includes("application/json")) {
        const data = await resp.json().catch(() => ({}));
        if (data?.pdfBase64) blob = b64toBlob(data.pdfBase64, "application/pdf");
        else if (data?.url) {
          window.location.href = data.url;
          return;
        } else throw new Error("JSON received but no pdfBase64/url.");
      } else blob = await resp.blob();

      const safeEvent = (eventName || "ticket").replace(/\s+/g, "-").slice(0, 50);
      downloadBlob(blob, `${safeEvent}-${bookingCode}.pdf`);
    } catch (err) {
      console.error("PDF download error:", err);
      alert(err?.message || "Could not download PDF. Please try again.");
    }
  }

  // Copy booking code to clipboard with small UI feedback
  const copyBookingCode = async (code, id) => {
    try {
      if (!code) return;
      await navigator.clipboard.writeText(code);
      setCopiedMap((m) => ({ ...m, [id]: true }));
      setTimeout(() => {
        setCopiedMap((m) => {
          const copy = { ...m };
          delete copy[id];
          return copy;
        });
      }, 1800);
    } catch (e) {
      console.error("copy failed", e);
    }
  };

  return (
    <div className="min-h-screen bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-amber-50 via-white to-yellow-50">
      <style>{`
        .fade-in { animation: fadeIn .4s ease both; }
        @keyframes fadeIn { from { opacity:.0; transform: translateY(5px) } to { opacity:1; transform: translateY(0) } }
      `}</style>

      {/* Page title strip — no search here (site header already has search) */}
      <header className="sticky top-0 z-20 border-b border-slate-200/90 bg-white/95 backdrop-blur-md">
        <div className="max-w-4xl mx-auto px-3 sm:px-4 py-2.5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-slate-800 min-w-0">
            <TicketIcon className="h-5 w-5 text-amber-600 shrink-0" />
            <h1 className="text-sm sm:text-base font-bold truncate">My Bookings</h1>
          </div>
          <span className="text-[11px] text-slate-500 tabular-nums shrink-0">Total {total}</span>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-3 sm:px-4 py-4 md:py-6">
        {/* Search — single prominent filter for this page */}
        <div className="relative mb-3">
          <input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Filter by event, booking code, venue…"
            className="w-full pl-9 pr-9 py-2 text-xs sm:text-sm rounded-xl border border-slate-200 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-amber-300/60 focus:border-amber-300"
          />
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
          {searchTerm ? (
            <button
              type="button"
              onClick={() => setSearchTerm("")}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 p-1"
              aria-label="Clear search"
            >
              <FaTimes className="text-sm" />
            </button>
          ) : null}
        </div>

        {/* Info bar */}
        <div className="mb-4 rounded-xl border border-slate-200/90 bg-white px-3 py-2.5 flex items-start gap-2 shadow-sm">
          <FaInfoCircle className="text-amber-600 shrink-0 mt-0.5 text-sm" />
          <p className="text-xs text-slate-600 leading-snug">
            Confirmed bookings include <strong className="text-slate-800">PDF</strong> for your M‑Ticket. Open <Link to="/tickets" className="font-semibold text-amber-800 hover:underline">My Tickets</Link> for the full list.
            {debouncedSearch ? (
              <span className="block mt-1 text-[11px] text-slate-500">
                Filter: <span className="font-semibold text-slate-700">{debouncedSearch}</span>
              </span>
            ) : null}
          </p>
        </div>

        {/* Loader */}
        {loading && (
          <div className="py-16 text-center">
            <FancyLoader label="Loading bookings…" />
          </div>
        )}

        {/* Error */}
        {errMsg && !loading && (
          <div className="mb-4 bg-rose-50 border border-rose-200 text-rose-800 rounded-xl p-3 text-sm">
            {errMsg}
          </div>
        )}

        {/* Grid — max 3 columns so cards stay readable */}
        {!loading && !!bookings.length && (
          <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            {bookings.map((b, idx) => {
              const status = b?.bookingStatus || "—";
              const confirmed = status?.toLowerCase() === "confirmed";
              const img = b?.eventId?.imageUrl || "";
              const title = b?.eventId?.name || "Untitled Event";
              const venue = b?.eventId?.venue?.name || "";
              const city = b?.eventId?.venue?.city || "";
              const state = b?.eventId?.venue?.state || "";
              // Prefer event date from backend (no shift)
              const eventDate = b?.eventId?.date || b?.eventDate || b?.createdAt;
              const bookingCode = b?.bookingCode || "—";
              const idKey = b?._id || b?.id || bookingCode || idx;

              const eventSlug = b?.eventId?._id || b?.eventId;

              return (
                <article
                  key={idKey}
                  className="group bg-white rounded-xl border border-slate-200/90 shadow-sm hover:shadow-md transition-shadow overflow-hidden fade-in flex flex-col"
                  style={{ animationDelay: `${idx * 30}ms` }}
                >
                  <div className="relative w-full h-28 sm:h-[7.25rem] overflow-hidden bg-slate-900 shrink-0">
                    {img ? (
                      <img
                        src={img}
                        alt=""
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="h-full w-full bg-gradient-to-br from-amber-100 to-yellow-100 flex flex-col items-center justify-center gap-1 text-amber-900/70">
                        <TicketIcon className="h-8 w-8 opacity-60" />
                        <span className="text-[10px] font-semibold uppercase tracking-wide">No poster</span>
                      </div>
                    )}
                    <div className="absolute inset-x-0 top-0 h-8 bg-gradient-to-b from-black/35 to-transparent pointer-events-none" />
                    <div className="absolute left-2 top-2 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wide text-white bg-black/45 backdrop-blur-sm border border-white/10">
                      {status}
                    </div>
                  </div>

                  <div className="p-3 flex flex-col gap-1.5 min-w-0 flex-1">
                    <h3 className="text-sm font-bold text-slate-900 leading-tight line-clamp-2">
                      {title}
                    </h3>

                    <div className="flex items-center gap-1.5 min-w-0">
                      <div className="font-mono text-[11px] text-slate-600 truncate tabular-nums">
                        {bookingCode}
                      </div>
                      <button
                        type="button"
                        onClick={() => copyBookingCode(bookingCode, idKey)}
                        className="shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold text-amber-800 hover:text-amber-950"
                        title="Copy booking code"
                      >
                        {copiedMap[idKey] ? (
                          <span className="inline-flex items-center gap-0.5 text-emerald-600">
                            <FaCheck className="text-[10px]" /> Copied
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-0.5">
                            <FaRegCopy className="text-[10px]" /> Copy
                          </span>
                        )}
                      </button>
                    </div>

                    <div className="text-[11px] text-slate-600 flex items-start gap-1.5 min-w-0 leading-snug">
                      <FaMapMarkerAlt className="text-amber-600 shrink-0 mt-0.5 text-[10px]" />
                      <span className="line-clamp-2">
                        {venue || "—"}
                        {venue && (city || state) ? " — " : ""}
                        {city}
                        {city && state ? ", " : ""}
                        {state}
                      </span>
                    </div>

                    <div className="text-[11px] text-slate-600 flex items-center gap-1.5 min-w-0">
                      <FaCalendarAlt className="text-amber-600 shrink-0 text-[10px]" />
                      <span className="truncate">
                        {formatDT(eventDate, { withTime: true, keepCalendarDate: true })}
                      </span>
                    </div>

                    <div className="mt-auto pt-2 flex items-center justify-between gap-2 border-t border-slate-100">
                      <div className="text-sm font-extrabold text-slate-900 tabular-nums">
                        {inr(b?.finalAmount)}
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        {confirmed && (
                          <button
                            type="button"
                            onClick={() => downloadTicketPDF(b?.bookingCode, title)}
                            className="inline-flex items-center gap-1 rounded-lg bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white text-[11px] font-bold px-2.5 py-1.5 shadow-sm"
                            title="Download Ticket PDF"
                          >
                            <FaDownload className="text-xs" />
                            PDF
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => setSelected(b)}
                          className="rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-[11px] font-semibold text-slate-700 px-2.5 py-1.5"
                        >
                          View
                        </button>
                        {eventSlug ? (
                          <Link
                            to={`/events/${eventSlug}`}
                            className="hidden sm:inline-flex rounded-lg border border-transparent text-[11px] font-semibold text-amber-800 hover:underline px-1 py-1.5"
                          >
                            Event
                          </Link>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}

        {/* Empty */}
        {!loading && !bookings.length && !errMsg && (
          <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-slate-600 shadow-sm">
            <div className="text-4xl mb-2">🎟️</div>
            <p className="font-semibold text-slate-800 text-sm">No bookings found</p>
            <p className="text-xs mt-1 text-slate-500">
              Book an event — it will appear here. Try clearing the filter above.
            </p>
          </div>
        )}

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div className="mt-6">
            <ReactPaginate
              breakLabel="…"
              nextLabel={
                <span className="px-2.5 py-1.5 rounded-md border border-slate-200 bg-white hover:bg-amber-50 hover:border-amber-200 text-xs font-medium">
                  Next ›
                </span>
              }
              previousLabel={
                <span className="px-2.5 py-1.5 rounded-md border border-slate-200 bg-white hover:bg-amber-50 hover:border-amber-200 text-xs font-medium">
                  ‹ Prev
                </span>
              }
              onPageChange={handlePageChange}
              forcePage={Math.max(0, Math.min(totalPages - 1, page - 1))}
              pageRangeDisplayed={5}
              marginPagesDisplayed={1}
              pageCount={totalPages}
              containerClassName="flex items-center justify-center gap-1 select-none flex-wrap"
              pageClassName="group"
              pageLinkClassName="px-2.5 py-1.5 rounded-md border border-slate-200 bg-white hover:bg-amber-50 hover:border-amber-200 transition-colors text-xs font-medium"
              breakClassName="px-2 text-xs"
              activeClassName="selected"
            />
            <style>{`.selected a{background:#f59e0b!important;color:#fff!important;border-color:#f59e0b!important}`}</style>
            <div className="mt-2 text-center text-[11px] text-slate-500">
              Page {page} of {totalPages} · {total} total
            </div>
          </div>
        )}
      </main>

      {/* Modal */}
      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 md:p-4">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setSelected(null)}
          />
          <div className="relative bg-white rounded-xl shadow-2xl border border-slate-200 w-full max-w-lg p-4 md:p-5 max-h-[88vh] overflow-y-auto">
            <button
              type="button"
              onClick={() => setSelected(null)}
              className="absolute top-2.5 right-2.5 w-8 h-8 flex items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-900"
              aria-label="Close"
            >
              ✕
            </button>

            <div className="flex items-start gap-3 mb-4 min-w-0 pr-8">
              <div className="w-24 shrink-0">
                <div className="w-full h-20 overflow-hidden rounded-lg border border-slate-200 bg-slate-100">
                  {selected?.eventId?.imageUrl ? (
                    <img
                      src={selected.eventId.imageUrl}
                      alt=""
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="h-full w-full flex items-center justify-center bg-amber-50">
                      <TicketIcon className="h-8 w-8 text-amber-300" />
                    </div>
                  )}
                </div>
              </div>

              <div className="min-w-0 flex-1">
                <h2 className="text-base font-bold text-slate-900 leading-tight line-clamp-2">
                  {selected?.eventId?.name}
                </h2>
                <p className="text-[11px] text-slate-600 flex items-start gap-1.5 mt-1 min-w-0 leading-snug">
                  <FaMapMarkerAlt className="text-amber-600 shrink-0 mt-0.5" />
                  <span>
                    {selected?.eventId?.venue?.name}
                    {selected?.eventId?.venue?.name &&
                      (selected?.eventId?.venue?.city ||
                        selected?.eventId?.venue?.state)
                      ? " — "
                      : ""}
                    {selected?.eventId?.venue?.city}
                    {selected?.eventId?.venue?.city &&
                      selected?.eventId?.venue?.state
                      ? ", "
                      : ""}
                    {selected?.eventId?.venue?.state}
                  </span>
                </p>
                <p className="text-[11px] text-slate-500 mt-1 flex items-center gap-1.5 min-w-0">
                  <FaCalendarAlt className="text-amber-600 shrink-0" />
                  <span className="truncate">
                    {formatDT(selected?.eventId?.date, {
                      withTime: true,
                      keepCalendarDate: true,
                    })}
                  </span>
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
              <Detail label="Booking Code" value={selected?.bookingCode} />
              <Detail label="Booking Status" value={selected?.bookingStatus} />
              <Detail label="Payment Status" value={selected?.paymentStatus} />
              <Detail label="Refund Status" value={selected?.refundStatus} />
              <Detail label="Payment Mode" value={selected?.paymentMode} />
              <Detail
                label="Tickets"
                value={selected?.ticketCategories
                  ?.map((t) =>
                    `${t?.quantity} x ${typeof t?.categoryId === "string"
                      ? t?.categoryId
                      : t?.categoryId?.name || ""
                    }`
                  )
                  .join(", ")}
              />
              <Detail label="Total Amount" value={inr(selected?.totalAmount)} />
              <Detail label="GST" value={inr(selected?.gstAmount)} />
              <Detail label="Convenience Fee" value={inr(selected?.convenienceFee)} />
              <Detail
                label="Discount"
                value={selected?.discountValue ? inr(selected?.discountValue) : "—"}
              />
              <Detail label="Final Amount" value={inr(selected?.finalAmount)} />
              <Detail
                label="Booked On"
                value={formatDT(selected?.createdAt, { withTime: true, mode: "localIST" })}
              />
            </div>

            <div className="mt-4 flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={() => setSelected(null)}
                className="rounded-lg border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
              >
                Close
              </button>
              {(selected?.bookingStatus || "").toLowerCase() === "confirmed" && (
                <button
                  type="button"
                  onClick={() =>
                    downloadTicketPDF(
                      selected?.bookingCode,
                      selected?.eventId?.name
                    )
                  }
                  className="rounded-lg bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 px-4 py-2 text-xs font-bold text-white shadow-sm"
                >
                  Download PDF
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* Small component */
function Detail({ label, value }) {
  return (
    <div className="bg-amber-50/50 border border-amber-100 rounded-lg px-2.5 py-2 min-w-0">
      <p className="text-[9px] font-bold text-amber-900/80 uppercase tracking-wide">
        {label}
      </p>
      <p className="font-medium text-slate-900 text-[11px] mt-0.5 break-words leading-snug">{value || "—"}</p>
    </div>
  );
}

